import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Divider,
    Paper,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';

// NUEVO: Importaciones para el selector de fecha personalizado
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/es'; // Importar localización en español para dayjs

// Formatea la fecha para mostrar como MM/YYYY
const formatearFechaDisplay = (fecha) => {
    if (!fecha) return '';
    const [year, month] = fecha.split('-');
    return `${month}/${year}`;
};

// Agrupa aportes consecutivos en rangos
const agruparAportesPorRango = (aportes) => {
    if (!aportes || aportes.length === 0) return [];

    const aportesOrdenados = [...aportes].sort((a, b) => new Date(a.fechaAporte) - new Date(b.fechaAporte));

    const rangos = [];
    let rangoActual = { 
        inicio: aportesOrdenados[0].fechaAporte, 
        fin: aportesOrdenados[0].fechaAporte,
        tipoAporte: aportesOrdenados[0].tipoAporte,
        empleador: aportesOrdenados[0].empleador
    };

    for (let i = 1; i < aportesOrdenados.length; i++) {
        const current = aportesOrdenados[i];
        const fechaAnterior = new Date(rangoActual.fin);
        const fechaActual = new Date(current.fechaAporte);

        const esConsecutivo = (fechaAnterior.getFullYear() === fechaActual.getFullYear() && fechaAnterior.getMonth() + 1 === fechaActual.getMonth()) ||
            (fechaAnterior.getFullYear() + 1 === fechaActual.getFullYear() && fechaAnterior.getMonth() === 11 && fechaActual.getMonth() === 0);

        const mismosDatos = current.tipoAporte === rangoActual.tipoAporte && 
                            current.empleador === rangoActual.empleador;

        if (esConsecutivo && mismosDatos) {
            rangoActual.fin = current.fechaAporte;
        } else {
            rangos.push(rangoActual);
            rangoActual = { 
                inicio: current.fechaAporte, 
                fin: current.fechaAporte,
                tipoAporte: current.tipoAporte,
                empleador: current.empleador
            };
        }
    }
    rangos.push(rangoActual);

    return rangos;
};

const GestionAportes = ({ aportes, onAportesChange, setSnackbar, readOnly = false, onNavigateHistorial }) => {
    // MODIFICADO: El estado ahora maneja objetos de fecha (o null)
    const [fechaDesde, setFechaDesde] = useState(null);
    const [fechaHasta, setFechaHasta] = useState(null);
    const [tipoAporte, setTipoAporte] = useState('Relación de Dependencia');
    const [empleador, setEmpleador] = useState('');
    const [cuitEmpleador, setCuitEmpleador] = useState('');
    const [esSicam, setEsSicam] = useState(false);

    const TIPOS_APORTE = [
        'Relación de Dependencia',
        'Monotributo',
        'Autónomo',
        'Servicio Doméstico',
        'Monotributo Social',
        'Ley 27.705',
        'SICAM/Moratoria',
        'Manual'
    ];

    const handleAgregarRango = () => {
        if (!fechaDesde || !fechaHasta) {
            setSnackbar({ open: true, message: 'Debe seleccionar un mes de inicio y fin.', severity: 'warning' });
            return;
        }

        // MODIFICADO: Usar directamente los objetos de fecha
        const inicio = fechaDesde.toDate();
        const fin = fechaHasta.toDate();

        if (inicio > fin) {
            setSnackbar({ open: true, message: 'La fecha de inicio no puede ser posterior a la fecha de fin.', severity: 'error' });
            return;
        }

        const nuevosAportesFechas = new Set(aportes.map(a => a.fechaAporte));
        const nuevosAportesMap = new Map();
        aportes.forEach(a => nuevosAportesMap.set(a.fechaAporte, a));

        let fechaActual = new Date(inicio.getFullYear(), inicio.getMonth(), 1);

        while (fechaActual <= fin) {
            const fechaFormateada = `${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, '0')}-01`;
            if (!nuevosAportesFechas.has(fechaFormateada)) {
                nuevosAportesFechas.add(fechaFormateada);
                nuevosAportesMap.set(fechaFormateada, {
                    fechaAporte: fechaFormateada,
                    tipoAporte: tipoAporte,
                    empleador: empleador || 'Carga Manual',
                    cuitEmpleador: cuitEmpleador,
                    esSicam: esSicam
                });
            }
            fechaActual.setMonth(fechaActual.getMonth() + 1);
        }

        const aportesActualizados = Array.from(nuevosAportesFechas)
            .map(fecha => nuevosAportesMap.get(fecha))
            .sort((a, b) => new Date(a.fechaAporte) - new Date(b.fechaAporte));

        onAportesChange(aportesActualizados);
        setSnackbar({ open: true, message: 'Rango de aportes agregado correctamente.', severity: 'success' });
        setFechaDesde(null);
        setFechaHasta(null);
    };

    const handleEliminarRango = (rango) => {
        const fechasAEliminar = new Set();
        let fechaActual = new Date(`${rango.inicio.substring(0, 7)}-02`);
        const fin = new Date(`${rango.fin.substring(0, 7)}-02`);

        while (fechaActual <= fin) {
            const fechaFormateada = `${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, '0')}-01`;
            fechasAEliminar.add(fechaFormateada);
            fechaActual.setMonth(fechaActual.getMonth() + 1);
        }

        const aportesFiltrados = aportes.filter(a => !fechasAEliminar.has(a.fechaAporte));
        onAportesChange(aportesFiltrados);
        setSnackbar({ open: true, message: 'Rango de aportes eliminado.', severity: 'info' });
    };

    const handleEliminarAporteIndividual = (fechaAporte) => {
        const aportesFiltrados = aportes.filter(a => a.fechaAporte !== fechaAporte);
        onAportesChange(aportesFiltrados);
        setSnackbar({ open: true, message: `Aporte de ${formatearFechaDisplay(fechaAporte)} eliminado.`, severity: 'info' });
    };

    const getMesesEnRango = (rango) => {
        const meses = [];
        let fechaActual = new Date(`${rango.inicio.substring(0, 7)}-02`);
        const fin = new Date(`${rango.fin.substring(0, 7)}-02`);

        while (fechaActual <= fin) {
            meses.push(`${fechaActual.getFullYear()}-${String(fechaActual.getMonth() + 1).padStart(2, '0')}-01`);
            fechaActual.setMonth(fechaActual.getMonth() + 1);
        }
        return meses;
    };

    const rangosDeAportes = useMemo(() => agruparAportesPorRango(aportes), [aportes]);

    return (
        <Paper elevation={2} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {readOnly ? 'Resumen de Aportes' : 'Gestionar Aportes'}
                    </Typography>
                    {readOnly && onNavigateHistorial && (
                        <Button variant="outlined" size="small" onClick={onNavigateHistorial}>
                            Ir al Historial Laboral
                        </Button>
                    )}
                </Stack>

                {!readOnly && (
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <Stack spacing={2} sx={{ mb: 2 }}>
                            <TextField 
                                select 
                                label="Tipo de Aporte" 
                                size="small" 
                                value={tipoAporte} 
                                onChange={(e) => setTipoAporte(e.target.value)}
                            >
                                {TIPOS_APORTE.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField 
                                    label="Empleador / Motivo" 
                                    size="small" 
                                    value={empleador} 
                                    onChange={(e) => setEmpleador(e.target.value)}
                                    fullWidth
                                />
                                <TextField 
                                    label="CUIT (Opcional)" 
                                    size="small" 
                                    value={cuitEmpleador} 
                                    onChange={(e) => setCuitEmpleador(e.target.value)}
                                    fullWidth
                                />
                            </Stack>

                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={esSicam} 
                                        onChange={(e) => setEsSicam(e.target.checked)} 
                                        size="small" 
                                    />
                                }
                                label="Es SICAM/Moratoria"
                            />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                                <DatePicker
                                    label="Desde (Mes/Año)"
                                    views={['year', 'month']}
                                    format="MM/YYYY"
                                    value={fechaDesde}
                                    onChange={(newValue) => setFechaDesde(newValue)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                                <DatePicker
                                    label="Hasta (Mes/Año)"
                                    views={['year', 'month']}
                                    format="MM/YYYY"
                                    value={fechaHasta}
                                    onChange={(newValue) => setFechaHasta(newValue)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={handleAgregarRango}
                                    sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'nowrap' }}
                                >
                                    Agregar Rango
                                </Button>
                            </Stack>
                        </Stack>
                    </LocalizationProvider>
                )}

                <Divider />

                <Box>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Aportes registrados: {aportes?.length || 0}
                    </Typography>
                    <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {rangosDeAportes.length > 0 ? (
                            rangosDeAportes.map((rango, index) => {
                                const textoRango = rango.inicio === rango.fin
                                    ? formatearFechaDisplay(rango.inicio)
                                    : `${formatearFechaDisplay(rango.inicio)} - ${formatearFechaDisplay(rango.fin)}`;
                                const mesesIndividuales = getMesesEnRango(rango);

                                return (
                                    <Accordion key={index} sx={{ '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                                                <Typography sx={{ flexShrink: 0 }}>
                                                    {textoRango} 
                                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                        ({rango.tipoAporte} {rango.empleador ? `- ${rango.empleador}` : ''})
                                                    </Typography>
                                                </Typography>
                                                {!readOnly && (
                                                    <Tooltip title="Eliminar rango completo">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEliminarRango(rango);
                                                            }}
                                                            sx={{ ml: 2 }}
                                                        >
                                                            <DeleteIcon fontSize="small" color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <List dense>
                                                {mesesIndividuales.map((mesObj, i) => {
                                                    const mesStr = typeof mesObj === 'string' ? mesObj : mesObj.fechaAporte || mesObj;
                                                    const aporteCompleto = aportes.find(a => a.fechaAporte === mesStr) || {};
                                                    const extraInfo = aporteCompleto.tipoAporte && aporteCompleto.tipoAporte !== 'Manual' 
                                                        ? ` - ${aporteCompleto.tipoAporte} (${aporteCompleto.empleador || 'Sin empleador'})` 
                                                        : '';
                                                    return (
                                                        <ListItem
                                                            key={i}
                                                            secondaryAction={
                                                                !readOnly && (
                                                                    <Tooltip title={`Eliminar aporte de ${formatearFechaDisplay(mesStr)}`}>
                                                                        <IconButton edge="end" onClick={() => handleEliminarAporteIndividual(mesStr)}>
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )
                                                            }
                                                        >
                                                            <ListItemText 
                                                                primary={formatearFechaDisplay(mesStr)} 
                                                                secondary={extraInfo || 'Carga manual'}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ p: 2 }}>
                                No hay aportes registrados.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
};

export default GestionAportes;
